import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Star, ArrowRight, Quote, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "residential", label: "Residential Cleaning" },
  { value: "commercial", label: "Commercial Cleaning" },
  { value: "airbnb", label: "Airbnb Cleaning" },
  { value: "post-renovation", label: "Post-Renovation Cleaning" },
  { value: "deep-cleaning", label: "Deep Cleaning" },
  { value: "move-out", label: "Move In/Out Cleaning" },
];

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    service: "",
    rating: 0,
    review: "",
    photo_url: "",
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData((prev) => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/testimonials/submit`, formData);
      setIsSubmitted(true);
      toast.success("Thank you for your testimonial!");
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      toast.error("Failed to submit testimonial. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      location: "",
      service: "",
      rating: 0,
      review: "",
      photo_url: "",
    });
    setIsSubmitted(false);
    setIsSubmitOpen(false);
  };

  return (
    <div data-testid="testimonials-page" className="bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
              Testimonials
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6">
              What Our Clients Say
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/80">
              Don't just take our word for it. Read what our satisfied clients have to say about their experience with Toronto Premium Cleaning.
            </p>
          </div>
        </div>
      </section>

      {/* Stats + Submit Button */}
      <section className="py-12 bg-white border-b border-[#E7E5E4]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div className="flex flex-wrap justify-center gap-12 md:gap-20">
              <div className="text-center">
                <p className="text-4xl font-bold text-[#064E3B] mb-1">4.9</p>
                <div className="flex gap-1 justify-center mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#C0A062] fill-[#C0A062]" />
                  ))}
                </div>
                <p className="text-[#78716C] text-sm">Average Rating</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-[#064E3B] mb-1">500+</p>
                <p className="text-[#78716C] text-sm">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-[#064E3B] mb-1">98%</p>
                <p className="text-[#78716C] text-sm">Would Recommend</p>
              </div>
            </div>
            
            {/* Submit Testimonial Button */}
            <Dialog open={isSubmitOpen} onOpenChange={(open) => {
              if (!open) resetForm();
              setIsSubmitOpen(open);
            }}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-6 py-3"
                  data-testid="submit-testimonial-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Share Your Experience
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                {!isSubmitted ? (
                  <>
                    <DialogHeader>
                      <DialogTitle className="text-2xl text-[#064E3B]">Share Your Experience</DialogTitle>
                      <DialogDescription className="text-[#78716C]">
                        We'd love to hear about your experience with Toronto Premium Cleaning. Your testimonial helps other clients make informed decisions.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your name"
                          required
                          data-testid="testimonial-name"
                        />
                      </div>
                      
                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          required
                          data-testid="testimonial-email"
                        />
                      </div>
                      
                      {/* Phone (optional) */}
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone (Optional)</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(647) 000-0000"
                          data-testid="testimonial-phone"
                        />
                      </div>
                      
                      {/* Location */}
                      <div className="space-y-2">
                        <Label htmlFor="location">Neighborhood/Area *</Label>
                        <Input
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="e.g., North York, Downtown Toronto"
                          required
                          data-testid="testimonial-location"
                        />
                      </div>
                      
                      {/* Service Type */}
                      <div className="space-y-2">
                        <Label>Service Type *</Label>
                        <Select
                          value={formData.service}
                          onValueChange={(value) => setFormData((prev) => ({ ...prev, service: value }))}
                          required
                        >
                          <SelectTrigger data-testid="testimonial-service">
                            <SelectValue placeholder="Select service type" />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Rating */}
                      <div className="space-y-2">
                        <Label>Rating *</Label>
                        <div className="flex gap-2" data-testid="testimonial-rating">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => handleRatingClick(star)}
                              onMouseEnter={() => setHoverRating(star)}
                              onMouseLeave={() => setHoverRating(0)}
                              className="p-1 transition-transform hover:scale-110"
                              data-testid={`rating-star-${star}`}
                            >
                              <Star
                                className={`w-8 h-8 transition-colors ${
                                  star <= (hoverRating || formData.rating)
                                    ? "text-[#C0A062] fill-[#C0A062]"
                                    : "text-gray-300"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Review */}
                      <div className="space-y-2">
                        <Label htmlFor="review">Your Review *</Label>
                        <Textarea
                          id="review"
                          name="review"
                          value={formData.review}
                          onChange={handleInputChange}
                          placeholder="Tell us about your experience..."
                          rows={4}
                          required
                          data-testid="testimonial-review"
                        />
                      </div>
                      
                      {/* Photo URL (optional) */}
                      <div className="space-y-2">
                        <Label htmlFor="photo_url">Photo URL (Optional)</Label>
                        <Input
                          id="photo_url"
                          name="photo_url"
                          value={formData.photo_url}
                          onChange={handleInputChange}
                          placeholder="Link to your photo (optional)"
                          data-testid="testimonial-photo"
                        />
                        <p className="text-xs text-[#78716C]">Share a photo of yourself or your cleaned space</p>
                      </div>
                      
                      <div className="pt-4 flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={resetForm}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="flex-1 bg-[#064E3B] hover:bg-[#065F46]"
                          disabled={isSubmitting}
                          data-testid="submit-review-btn"
                        >
                          {isSubmitting ? "Submitting..." : "Submit Review"}
                        </Button>
                      </div>
                      
                      <p className="text-xs text-center text-[#78716C]">
                        Your testimonial will be reviewed before being published.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-[#064E3B]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#0C0A09] mb-2">
                      Thank You!
                    </h3>
                    <p className="text-[#78716C] mb-6">
                      Your testimonial has been submitted successfully. It will appear on our website once approved by our team.
                    </p>
                    <Button
                      onClick={resetForm}
                      className="bg-[#064E3B] hover:bg-[#065F46]"
                      data-testid="close-success-btn"
                    >
                      Close
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-32 bg-[#F0FDF4]">
          <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
            <Quote className="w-16 h-16 mx-auto mb-8 text-[#C0A062]" />
            <blockquote className="text-2xl md:text-3xl font-medium text-[#0C0A09] leading-relaxed mb-8 italic">
              "{testimonials[0]?.review}"
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#064E3B] flex items-center justify-center text-white text-xl font-semibold overflow-hidden">
                {testimonials[0]?.image_url ? (
                  <img src={testimonials[0].image_url} alt={testimonials[0]?.name} className="w-full h-full object-cover" />
                ) : (
                  testimonials[0]?.name?.charAt(0)
                )}
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#0C0A09]">{testimonials[0]?.name}</p>
                <p className="text-[#78716C] text-sm">{testimonials[0]?.location} • {testimonials[0]?.service}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All Testimonials Grid */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-4">
              More Happy Clients
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(1).map((testimonial) => (
              <div
                key={testimonial.id}
                className="testimonial-card bg-white p-8 rounded-2xl shadow-lg border border-[#E7E5E4]/50 hover:border-[#C0A062]/50 transition-colors"
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`star-${testimonial.id}-${i}`} className="w-5 h-5 text-[#C0A062] fill-[#C0A062]" />
                  ))}
                </div>
                <p className="text-[#78716C] leading-relaxed mb-6 italic">
                  "{testimonial.review}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#E7E5E4]">
                  <div className="w-12 h-12 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-semibold overflow-hidden">
                    {testimonial.image_url ? (
                      <img src={testimonial.image_url} alt={testimonial.name} className="w-full h-full object-cover" />
                    ) : (
                      testimonial.name.charAt(0)
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-[#0C0A09]">{testimonial.name}</p>
                    <p className="text-[#78716C] text-sm">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
            Join Our Happy Clients
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Experience the exceptional service that has earned us hundreds of 5-star reviews. Book your cleaning today!
          </p>
          <Link to="/booking" data-testid="testimonials-book-btn">
            <Button className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-[#C0A062]/30">
              Book Your Cleaning
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
